import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Users2, PlusCircle, Play, FileText, Vote, CheckCircle, XCircle,
    Download, ChevronDown, ChevronRight,
} from "lucide-react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../../components/ui/select";

const ESTADO_COLOR = {
    convocada: "text-slate-700 bg-slate-50 border-slate-200",
    en_curso: "text-blue-700 bg-blue-50 border-blue-200",
    cerrada: "text-emerald-700 bg-emerald-50 border-emerald-200",
};

const RESOL_LABEL = {
    aprobado: { label: "Aprobado", cls: "text-emerald-700 bg-emerald-50 border-emerald-200" },
    aprobado_con_condiciones: { label: "Aprobado c/condiciones", cls: "text-amber-700 bg-amber-50 border-amber-200" },
    rechazado: { label: "Rechazado", cls: "text-red-700 bg-red-50 border-red-200" },
    diferido: { label: "Diferido", cls: "text-slate-700 bg-slate-50 border-slate-200" },
};

const TIPO_ASUNTO = [
    { value: "dictamen_aceptacion", label: "Dictamen de aceptación" },
    { value: "revision_riesgos", label: "Revisión de riesgos" },
    { value: "excepcion_expediente", label: "Excepción de expediente" },
    { value: "otro", label: "Otro" },
];

export default function ComiteSesiones() {
    const [sesiones, setSesiones] = useState([]);
    const [miembros, setMiembros] = useState([]);
    const [openNew, setOpenNew] = useState(false);
    const [expandido, setExpandido] = useState(null);
    const [formSesion, setFormSesion] = useState({
        tipo: "ordinaria",
        fecha_convocatoria: new Date().toISOString().slice(0, 10),
        fecha_sesion: new Date().toISOString().slice(0, 10),
        orden_del_dia: "",
    });
    const [asistentes, setAsistentes] = useState([]);
    const [nuevoAsunto, setNuevoAsunto] = useState(null); // sesion_id
    const [asuntoForm, setAsuntoForm] = useState({
        tipo: "dictamen_aceptacion", referencia: "", descripcion: "",
    });
    const [votando, setVotando] = useState(null); // {sesion_id, asunto_id}
    const [votoForm, setVotoForm] = useState({
        resolucion: "aprobado", a_favor: 0, en_contra: 0, abstenciones: 0, condiciones: "",
    });

    const loadAll = async () => {
        try {
            const [s, m] = await Promise.all([
                api.get("/gobierno/comite/sesiones"),
                api.get("/gobierno/comite/miembros"),
            ]);
            setSesiones(s.data);
            setMiembros(m.data);
        } catch (err) {
            toast.error("No se pudo cargar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    useEffect(() => { loadAll(); }, []);

    const crearSesion = async () => {
        try {
            const payload = {
                ...formSesion,
                orden_del_dia: formSesion.orden_del_dia
                    .split("\n").map((s) => s.trim()).filter(Boolean),
            };
            await api.post("/gobierno/comite/sesiones", payload);
            toast.success("Sesión convocada");
            setOpenNew(false);
            setFormSesion({
                tipo: "ordinaria",
                fecha_convocatoria: new Date().toISOString().slice(0, 10),
                fecha_sesion: new Date().toISOString().slice(0, 10),
                orden_del_dia: "",
            });
            loadAll();
        } catch (err) {
            toast.error("No se pudo crear", {
                description: err?.response?.data?.detail,
            });
        }
    };

    const iniciarSesion = async (sesion_id) => {
        if (asistentes.length === 0) {
            toast.error("Selecciona los miembros asistentes");
            return;
        }
        try {
            await api.post(
                `/gobierno/comite/sesiones/${sesion_id}/iniciar`,
                asistentes,
            );
            toast.success("Sesión iniciada · quórum cumplido");
            setAsistentes([]);
            loadAll();
        } catch (err) {
            toast.error("No se pudo iniciar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    const agregarAsunto = async (sesion_id) => {
        try {
            await api.post(
                `/gobierno/comite/sesiones/${sesion_id}/asuntos`,
                asuntoForm,
            );
            toast.success("Asunto agregado");
            setNuevoAsunto(null);
            setAsuntoForm({ tipo: "dictamen_aceptacion", referencia: "", descripcion: "" });
            loadAll();
        } catch (err) {
            toast.error("No se pudo agregar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    const votar = async () => {
        try {
            const payload = {
                ...votoForm,
                condiciones: votoForm.condiciones
                    .split("\n").map((s) => s.trim()).filter(Boolean),
            };
            await api.post(
                `/gobierno/comite/sesiones/${votando.sesion_id}/asuntos/${votando.asunto_id}/votar`,
                payload,
            );
            toast.success("Votación registrada");
            setVotando(null);
            setVotoForm({
                resolucion: "aprobado", a_favor: 0, en_contra: 0, abstenciones: 0, condiciones: "",
            });
            loadAll();
        } catch (err) {
            toast.error("No se pudo votar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    const cerrarSesion = async (sesion_id) => {
        try {
            const { data } = await api.post(`/gobierno/comite/sesiones/${sesion_id}/cerrar`);
            toast.success(`Acta generada · ${data.size_bytes} bytes · hash ${data.hash_acta.slice(0, 12)}…`);
            loadAll();
        } catch (err) {
            toast.error("No se pudo cerrar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    const descargarActa = async (sesion_id, numero_sesion) => {
        try {
            const { data } = await api.get(
                `/gobierno/comite/sesiones/${sesion_id}/acta.pdf`,
                { responseType: "blob" },
            );
            const url = URL.createObjectURL(new Blob([data], { type: "application/pdf" }));
            const a = document.createElement("a");
            a.href = url;
            a.download = `Acta_${numero_sesion}.pdf`;
            a.click();
            URL.revokeObjectURL(url);
        } catch (err) {
            toast.error("No se pudo descargar", {
                description: err?.response?.data?.detail,
            });
        }
    };

    return (
        <div className="p-8 lg:p-10 space-y-6 max-w-[1600px]" data-testid="comite-sesiones-page">
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div>
                    <div className="text-[11px] uppercase tracking-[0.3em] text-[color:var(--brand-dark-gold)] font-semibold mb-2">
                        Gobierno · Fase 14
                    </div>
                    <h1 className="font-display text-4xl tracking-tight flex items-center gap-3">
                        <Users2 className="h-8 w-8 text-[color:var(--brand-dark-gold)]" />
                        Sesiones del Comité Fiduciario
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
                        Ciclo: convocatoria → apertura con quórum → asuntos → votación → cierre + acta PDF.
                    </p>
                </div>
                <Button onClick={() => setOpenNew(true)} data-testid="new-sesion">
                    <PlusCircle className="h-3.5 w-3.5 mr-2" /> Nueva convocatoria
                </Button>
            </div>

            <div className="space-y-3">
                {sesiones.map((s) => (
                    <div key={s.id} className="kpi-card" data-testid={`sesion-${s.numero_sesion}`}>
                        <div className="flex items-center gap-3 flex-wrap">
                            <button
                                onClick={() => setExpandido(expandido === s.id ? null : s.id)}
                                className="flex items-center gap-2 flex-1 min-w-0"
                                data-testid={`toggle-${s.numero_sesion}`}
                            >
                                {expandido === s.id ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                <div className="font-display text-lg">{s.numero_sesion}</div>
                                <span className="text-xs text-muted-foreground">
                                    {s.tipo} · sesión: {s.fecha_sesion?.slice(0, 10)}
                                </span>
                            </button>
                            <span
                                className={`text-[11px] uppercase tracking-widest border rounded-md px-2 py-1 ${ESTADO_COLOR[s.estado] || ""}`}
                            >
                                {s.estado}
                            </span>
                            <span className="text-xs text-muted-foreground">
                                Quórum: {s.quorum?.asistentes?.length || 0}/{s.quorum?.requerido}
                            </span>
                            {s.estado === "cerrada" && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => descargarActa(s.id, s.numero_sesion)}
                                    data-testid={`descargar-acta-${s.numero_sesion}`}
                                >
                                    <Download className="h-3 w-3 mr-1" /> Acta PDF
                                </Button>
                            )}
                        </div>

                        {expandido === s.id && (
                            <div className="mt-4 space-y-4">
                                <div>
                                    <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">
                                        Orden del día
                                    </div>
                                    <ol className="text-sm list-decimal ml-5 space-y-1">
                                        {(s.orden_del_dia || []).map((item, i) => (
                                            <li key={i}>{item}</li>
                                        ))}
                                    </ol>
                                </div>

                                {s.estado === "convocada" && (
                                    <div className="border border-border rounded-md p-3 bg-muted/30">
                                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground mb-2">
                                            Iniciar sesión · seleccionar asistentes
                                        </div>
                                        <div className="grid grid-cols-2 gap-2 mb-2">
                                            {miembros.map((m) => (
                                                <label
                                                    key={m.miembro_id}
                                                    className="flex items-center gap-2 text-sm"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={asistentes.includes(m.miembro_id)}
                                                        onChange={(e) =>
                                                            setAsistentes(
                                                                e.target.checked
                                                                    ? [...asistentes, m.miembro_id]
                                                                    : asistentes.filter((a) => a !== m.miembro_id),
                                                            )
                                                        }
                                                        data-testid={`asistente-${m.rol_comite}`}
                                                    />
                                                    {m.nombre}
                                                </label>
                                            ))}
                                        </div>
                                        <Button
                                            size="sm"
                                            onClick={() => iniciarSesion(s.id)}
                                            data-testid={`iniciar-${s.numero_sesion}`}
                                        >
                                            <Play className="h-3 w-3 mr-1" /> Iniciar (quórum {s.quorum?.requerido})
                                        </Button>
                                    </div>
                                )}

                                <div>
                                    <div className="flex items-center justify-between mb-2">
                                        <div className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                            Asuntos ({(s.asuntos || []).length})
                                        </div>
                                        {s.estado === "en_curso" && (
                                            <Button
                                                size="sm"
                                                variant="outline"
                                                onClick={() => setNuevoAsunto(s.id)}
                                                data-testid={`add-asunto-${s.numero_sesion}`}
                                            >
                                                <PlusCircle className="h-3 w-3 mr-1" /> Añadir asunto
                                            </Button>
                                        )}
                                    </div>
                                    <table className="trf-table">
                                        <thead>
                                            <tr>
                                                <th>Tipo</th>
                                                <th>Referencia</th>
                                                <th>Descripción</th>
                                                <th>Resolución</th>
                                                <th className="text-center">Votos (A/C/Ab)</th>
                                                <th />
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(s.asuntos || []).map((a) => (
                                                <tr key={a.asunto_id} data-testid={`asunto-${a.asunto_id}`}>
                                                    <td className="text-xs">{a.tipo?.replace(/_/g, " ")}</td>
                                                    <td className="text-xs font-mono">{a.referencia}</td>
                                                    <td className="text-xs">{a.descripcion}</td>
                                                    <td>
                                                        {a.resolucion ? (
                                                            <span
                                                                className={`text-[10px] uppercase tracking-widest border rounded-md px-2 py-1 ${RESOL_LABEL[a.resolucion]?.cls || ""}`}
                                                            >
                                                                {RESOL_LABEL[a.resolucion]?.label || a.resolucion}
                                                            </span>
                                                        ) : (
                                                            <span className="text-xs text-muted-foreground">Pendiente</span>
                                                        )}
                                                    </td>
                                                    <td className="text-center tabular text-xs">
                                                        {a.votacion
                                                            ? `${a.votacion.a_favor}/${a.votacion.en_contra}/${a.votacion.abstenciones}`
                                                            : "—"}
                                                    </td>
                                                    <td>
                                                        {s.estado === "en_curso" && !a.resolucion && (
                                                            <Button
                                                                size="sm"
                                                                variant="outline"
                                                                onClick={() =>
                                                                    setVotando({ sesion_id: s.id, asunto_id: a.asunto_id })
                                                                }
                                                                data-testid={`votar-${a.asunto_id}`}
                                                            >
                                                                <Vote className="h-3 w-3 mr-1" /> Votar
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>

                                {s.estado === "en_curso" && (
                                    <Button
                                        size="sm"
                                        onClick={() => cerrarSesion(s.id)}
                                        data-testid={`cerrar-${s.numero_sesion}`}
                                    >
                                        <FileText className="h-3 w-3 mr-1" /> Cerrar y generar acta
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                ))}
                {sesiones.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground text-sm">
                        Sin sesiones registradas. Convoca la primera.
                    </div>
                )}
            </div>

            {/* Dialogo nueva sesión */}
            <Dialog open={openNew} onOpenChange={setOpenNew}>
                <DialogContent className="max-w-lg">
                    <DialogHeader><DialogTitle>Nueva convocatoria</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <Select
                            value={formSesion.tipo}
                            onValueChange={(v) => setFormSesion({ ...formSesion, tipo: v })}
                        >
                            <SelectTrigger data-testid="sesion-tipo"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="ordinaria">Ordinaria</SelectItem>
                                <SelectItem value="extraordinaria">Extraordinaria</SelectItem>
                            </SelectContent>
                        </Select>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                    Fecha convocatoria
                                </label>
                                <Input
                                    type="date"
                                    value={formSesion.fecha_convocatoria}
                                    onChange={(e) => setFormSesion({ ...formSesion, fecha_convocatoria: e.target.value })}
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                    Fecha sesión
                                </label>
                                <Input
                                    type="date"
                                    value={formSesion.fecha_sesion}
                                    onChange={(e) => setFormSesion({ ...formSesion, fecha_sesion: e.target.value })}
                                    data-testid="sesion-fecha"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-[11px] uppercase tracking-widest text-muted-foreground">
                                Orden del día (una línea por punto)
                            </label>
                            <Textarea
                                value={formSesion.orden_del_dia}
                                onChange={(e) => setFormSesion({ ...formSesion, orden_del_dia: e.target.value })}
                                rows={5}
                                data-testid="sesion-orden"
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setOpenNew(false)}>Cancelar</Button>
                        <Button onClick={crearSesion} data-testid="submit-sesion">Convocar</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialogo nuevo asunto */}
            <Dialog open={!!nuevoAsunto} onOpenChange={(o) => !o && setNuevoAsunto(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Añadir asunto</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <Select
                            value={asuntoForm.tipo}
                            onValueChange={(v) => setAsuntoForm({ ...asuntoForm, tipo: v })}
                        >
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                                {TIPO_ASUNTO.map((t) => (
                                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Input
                            value={asuntoForm.referencia}
                            onChange={(e) => setAsuntoForm({ ...asuntoForm, referencia: e.target.value })}
                            placeholder="Referencia (F-01, ID fideicomiso, etc)"
                            data-testid="asunto-ref"
                        />
                        <Textarea
                            value={asuntoForm.descripcion}
                            onChange={(e) => setAsuntoForm({ ...asuntoForm, descripcion: e.target.value })}
                            placeholder="Descripción"
                            rows={3}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setNuevoAsunto(null)}>Cancelar</Button>
                        <Button onClick={() => agregarAsunto(nuevoAsunto)} data-testid="submit-asunto">
                            Añadir
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Dialogo votación */}
            <Dialog open={!!votando} onOpenChange={(o) => !o && setVotando(null)}>
                <DialogContent>
                    <DialogHeader><DialogTitle>Registrar votación</DialogTitle></DialogHeader>
                    <div className="space-y-3">
                        <Select
                            value={votoForm.resolucion}
                            onValueChange={(v) => setVotoForm({ ...votoForm, resolucion: v })}
                        >
                            <SelectTrigger data-testid="voto-resolucion"><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value="aprobado">Aprobado</SelectItem>
                                <SelectItem value="aprobado_con_condiciones">Aprobado con condiciones</SelectItem>
                                <SelectItem value="rechazado">Rechazado</SelectItem>
                                <SelectItem value="diferido">Diferido</SelectItem>
                            </SelectContent>
                        </Select>
                        <div className="grid grid-cols-3 gap-2">
                            <div>
                                <label className="text-[11px] uppercase tracking-widest text-muted-foreground">A favor</label>
                                <Input
                                    type="number" min={0}
                                    value={votoForm.a_favor}
                                    onChange={(e) => setVotoForm({ ...votoForm, a_favor: Number(e.target.value) })}
                                    data-testid="voto-favor"
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-widest text-muted-foreground">En contra</label>
                                <Input
                                    type="number" min={0}
                                    value={votoForm.en_contra}
                                    onChange={(e) => setVotoForm({ ...votoForm, en_contra: Number(e.target.value) })}
                                />
                            </div>
                            <div>
                                <label className="text-[11px] uppercase tracking-widest text-muted-foreground">Abstenciones</label>
                                <Input
                                    type="number" min={0}
                                    value={votoForm.abstenciones}
                                    onChange={(e) => setVotoForm({ ...votoForm, abstenciones: Number(e.target.value) })}
                                />
                            </div>
                        </div>
                        <Textarea
                            value={votoForm.condiciones}
                            onChange={(e) => setVotoForm({ ...votoForm, condiciones: e.target.value })}
                            placeholder="Condiciones (una por línea, opcional)"
                            rows={2}
                        />
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setVotando(null)}>Cancelar</Button>
                        <Button onClick={votar} data-testid="submit-voto">Registrar votación</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
